// Single Card Feed Container
// Reusable container for single-card snap scroll view across all feed tabs

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FeedItem, FeedInteraction } from "../../../types/socialFeed";
import { FeedItemRenderer } from "../FeedItems/FeedItemRenderer";
import {
  SingleCardFeedContainer as StyledContainer,
  SingleCardScrollContainer,
  SingleCardWrapper,
  SingleCardInnerWrapper,
  LoadingContainer,
  LoadingSpinner,
  EmptyState,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateDescription,
} from "../../../styles/SocialFeed/SingleCardFeed.styles";

interface SingleCardFeedContainerProps {
  items: FeedItem[];
  userId: string;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onItemInteraction: (interaction: FeedInteraction) => void;
  onUserClick: (userId: string) => void;
  onSwipeModeTrigger?: (item: FeedItem) => void;
}

export const SingleCardFeedContainer: React.FC<SingleCardFeedContainerProps> = ({
  items,
  userId,
  isLoading,
  hasMore,
  onLoadMore,
  onItemInteraction,
  onUserClick,
  onSwipeModeTrigger,
}) => {
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);

  // Handle scroll to detect when to load more
  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const container = e.currentTarget;
      const { scrollTop, scrollHeight, clientHeight } = container;
      
      // Prevent division by zero
      const maxScroll = scrollHeight - clientHeight;
      if (maxScroll <= 0) return;
      
      const scrollPercentage = scrollTop / maxScroll;

      // Calculate which card is currently visible based on scroll position
      // Each card is exactly clientHeight tall
      const cardHeight = clientHeight;
      const newIndex = Math.round(scrollTop / cardHeight);
      setCurrentCardIndex(newIndex);

      // Load more when near bottom (80% scrolled)
      if (scrollPercentage > 0.8 && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  if (isLoading && items.length === 0) {
    return (
      <StyledContainer>
        <LoadingContainer>
          <LoadingSpinner />
          <p>Loading feed...</p>
        </LoadingContainer>
      </StyledContainer>
    );
  }

  if (items.length === 0) {
    return (
      <StyledContainer>
        <EmptyState>
          <EmptyStateIcon>📱</EmptyStateIcon>
          <EmptyStateTitle>No content yet</EmptyStateTitle>
          <EmptyStateDescription>
            Start playing Heart Games or complete your profile to see content in your feed.
          </EmptyStateDescription>
        </EmptyState>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer>
      {/* Scroll Container - One card at a time with snap scrolling */}
      <SingleCardScrollContainer
        ref={scrollContainerRef}
        onScroll={handleScroll}
      >
        {items.map((item, index) => (
          <SingleCardWrapper key={item.id}>
            <SingleCardInnerWrapper>
              <FeedItemRenderer
                item={item}
                onInteraction={onItemInteraction}
                onUserClick={onUserClick}
                onSwipeModeTrigger={onSwipeModeTrigger}
                showSwipeOption={item.metadata?.showSwipeOption}
                isFullScreen={false}
                aspectRatio={item.content.media?.aspectRatio}
              />
            </SingleCardInnerWrapper>
          </SingleCardWrapper>
        ))}

        {/* Loading More Indicator */}
        {isLoading && items.length > 0 && (
          <LoadingContainer>
            <LoadingSpinner />
            <p>Loading more...</p>
          </LoadingContainer>
        )}
      </SingleCardScrollContainer>
    </StyledContainer>
  );
};

